import { 
  Box, 
  Button, 
  Checkbox, 
  Divider, 
  FormControl, 
  FormControlLabel, 
  FormHelperText, 
  Grid, 
  InputAdornment, 
  InputLabel, 
  MenuItem, 
  Radio, 
  RadioGroup, 
  Select, 
  TextField, 
  Typography 
} from "@mui/material"
import { AttorneyContactInfo, AttorneysInfo, AttorneyType, LenderInfo, OrderRequestFormInputs, PartyFields, PartyGroups, PersonInfo } from "../../../types/orderRequest"
import { OrderRequestField, orderRequestFieldTypes } from "../../../types/portalTypes/PortalDomainOrg"
import { useState, useRef, forwardRef } from 'react'
import { Control, Controller, ControllerFieldState, useFieldArray, useWatch } from "react-hook-form"
import { AddCircleOutline } from "@mui/icons-material"

export const ORDER_TYPES = [{FriendlyName: 'Purchase', SoftProName: 'Purchase'}, {FriendlyName: 'Refinance', SoftProName: 'Refinance'}, {FriendlyName: 'Equity Loan / HELOC', SoftProName: 'Equity'}, {FriendlyName: 'Other', SoftProName: 'Other'}]
export const AGENT_ROLES = ["Buyer's Agent", "Seller's Agent", 'Attorney', 'Lender', 'Other'] as const
export const BUYSIDE_ROLES: typeof AGENT_ROLES[number][] = ["Buyer's Agent", "Lender"]
export const SELLSIDE_ROLES: typeof AGENT_ROLES[number][] = ["Seller's Agent"]
export const NEEDSSIDE_ROLES: typeof AGENT_ROLES[number][] = ["Attorney", "Other"]
export const ORGANIZATION_TYPES = ['Trust', 'Corporation', 'Limited Liability Corp.', 'Limited Liability Company', 'Limited Partnership', 'Partnership', 'Estate'] as const
export const PROPERTY_TYPES = ['Single Family', '1-4 Family', 'PUD', 'Condo', 'Cooperative', 'Unimproved', 'Vacant Land', 'Other']
export const FINANCING_TYPES = ['Conventional', 'FHA', 'VA']
export const ATTORNEY_TYPES: AttorneyType[] = ['Buyer', 'Seller', 'Lender', 'Unassigned']
/** e.g. "Buyer Attorney"; an unassigned attorney isn't tied to a participant, so it's just "Attorney" */
export const attorneyLabel = (type: AttorneyType) => (type === 'Unassigned' ? 'Attorney' : `${type} Attorney`)
/** e.g. "Buyer's Attorney", for read-only views */
export const attorneyPossessiveLabel = (type: AttorneyType) => (type === 'Unassigned' ? 'Attorney' : `${type}'s Attorney`)
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']
export const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf']
export const ACCEPT = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join()

// Same pattern the backend's z.email() validates with, so an address accepted here isn't rejected on submit
export const EMAIL_PATTERN = {
  value: /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/,
  message: 'Please enter a valid email address',
}

export const PHONE_PATTERN = {
  value: /^(\+|\s|\d){0,3}(\(|-|\s)?\d{3}(\)|-|\s){0,2}\d{3}(-|\s)?\d{4,5}$/,
  message: 'Please enter a valid phone number.',
}

/*
 * Text rules mirroring the backend's order request schema
 * (gondor: packages/order-request-client/types/OrderRequestPayloadSchema.ts), so users see why a value
 * is rejected inline instead of a failed submit. Keep the limits and checks below in sync with that schema.
 */
const URL_REGEX = /https?:\/\/|www\./i
const HTML_CHARS_REGEX = /[<>]/
const SCRIPT_PREFIX_REGEX = /^\s*(javascript|data|vbscript):/i

type TextValidator = (value?: string) => true | string

const noUrl: TextValidator = (v) => !v || !URL_REGEX.test(v) || 'Web addresses (URLs) are not allowed in this field'
const noHtmlChars: TextValidator = (v) => !v || !HTML_CHARS_REGEX.test(v) || 'The < and > characters are not allowed in this field'
const noScriptPrefix: TextValidator = (v) => !v || !SCRIPT_PREFIX_REGEX.test(v) || 'This field can\'t start with "javascript:", "data:", or "vbscript:"'

/** react-hook-form maxLength rule with a message that states the limit */
export const maxLengthRule = (max: number) => ({
  value: max,
  message: `Must be ${max.toLocaleString()} characters or fewer`,
})

const textRules = (max: number, validate: Record<string, TextValidator> = {}) => ({ maxLength: maxLengthRule(max), validate })

export const TEXT_RULES = {
  /** Person first/middle/last names */
  Name: textRules(200, { noUrl, noHtmlChars, noScriptPrefix }),
  /** Organization party name */
  OrganizationName: textRules(300, { noUrl, noHtmlChars, noScriptPrefix }),
  /** Submitter / attorney company name */
  CompanyName: textRules(300, { noUrl }),
  /** Address lines (property, party, company) */
  Address: textRules(500, { noUrl, noHtmlChars }),
  City: textRules(100),
  Email: textRules(320),
  LenderName: textRules(300),
  LoanAmount: textRules(50),
  LoanNumber: textRules(100),
  OtherOrderType: textRules(200),
  LegalDescription: textRules(2000),
  TaxId: textRules(50),
  Comments: textRules(5000),
  // URLs are allowed in custom field values (e.g. a listing link), matching the backend
  CustomFieldValue: textRules(10000, { noHtmlChars, noScriptPrefix }),
}

/** Error props for a field whose "required" rule only applies sometimes: hides a stale required error, but always shows length/format errors */
export const fieldErrorProps = (fieldState: ControllerFieldState, required?: boolean) => {
  const show = !!fieldState.error && (!!required || fieldState.error.type !== 'required')
  return { error: show, helperText: show ? fieldState.error?.message : undefined }
}

export const ZIP_PATTERN = {
  value: /^\d{5}(-\d{4})?$/,
  message: 'Please enter a valid zip code',
}

export const LOAN_AMOUNT_PATTERN = {
  value: /^\d+(\.\d{1,2})?$/,
  message: 'Please enter a valid amount',
}

const defaultPerson: PersonInfo = {
  FirstName: '',
  MiddleName: '',
  LastName: '',
  Email: '',
  Phone: '',
}

const defaultAddress: PartyFields['Address'] = {
  SameAsProperty: false,
  Address1: '',
  Address2: '',
  City: '',
  State: '',
  Zip: '',
}

export const defaultParties: PartyGroups = {
  Buyer: [
    {
      Type: 'individual',
      OrganizationName: '',
      OrganizationType: '',
      Primary: { ...defaultPerson },
      Spouse: { ...defaultPerson },
      OrgPeople: [{ ...defaultPerson }],
      Address: { ...defaultAddress },
    }
  ],
  Seller: [
    {
      Type: 'individual',
      OrganizationName: '',
      OrganizationType: '',
      Primary: { ...defaultPerson },
      Spouse: { ...defaultPerson },
      OrgPeople: [{ ...defaultPerson }],
      Address: { ...defaultAddress },
    }
  ]
}

export const defaultLenders: LenderInfo[] = [
  {
    LenderName: '',
    LoanAmount: '',
    LoanNumber: '',
    FinancingType: '',
    LenderFirstName: '',
    LenderLastName: '',
    LenderEmail: '',
    LenderPhone: ''
  }
]

export const defaultAttorney: AttorneyContactInfo = {
  FirstName: '',
  LastName: '',
  Email: '',
  Phone: '',
  Fax: '',
  CompanyName: '',
  CompanyAddress1: '',
  CompanyAddress2: '',
  CompanyCity: '',
  CompanyState: '',
  CompanyZip: '',
}

export const textFieldProps = {
  variant: 'outlined' as const,
  fullWidth: true,
  sx: { '& .MuiOutlinedInput-root': { backgroundColor: 'white' } },
  margin: 'normal' as const,
}

export const selectFieldProps = {
  sx: { backgroundColor: 'white' },
  MenuProps: {
    PaperProps: {
      sx: {
        maxHeight: 300, // Limit the visual length in pixels
      },
    },
  }
}

/** Spread onto any <Checkbox> to allow Enter to toggle it without submitting the form */
export function checkboxEnterKeyDown(field: { value: boolean | undefined | null; onChange: (v: boolean) => void }) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      field.onChange(!field.value)
    }
  }
}

export const OpenOnFocusSelect = forwardRef<HTMLDivElement, React.ComponentProps<typeof Select>>(
  function OpenOnFocusSelect({ children, onFocus, ...props }, ref) {
    const [open, setOpen] = useState(false)
    const justClosedRef = useRef(false)
    return (
      <Select
        {...props}
        ref={ref}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          justClosedRef.current = true
          setOpen(false)
          // Reset after the focus event that follows menu close has fired
          setTimeout(() => { justClosedRef.current = false }, 0)
        }}
        onFocus={(e) => {
          if (!justClosedRef.current) setOpen(true)
          onFocus?.(e as any)
        }}
      >
        {children}
      </Select>
    )
  }
)

export function SectionHeader({ title }: { title: string }) {
  return (
    <Typography variant="h6">
      {title}
    </Typography>
  )
}

export function StateSelect({
  name,
  control,
  required,
}: {
  name: any
  control: Control<OrderRequestFormInputs>
  required?: boolean
}) {
  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: 'State is required' } : undefined}
      render={({ field, fieldState }) => (
        <FormControl fullWidth margin="normal" required={required} error={!!fieldState.error}>
          <InputLabel>State</InputLabel>
          <OpenOnFocusSelect {...field} {...selectFieldProps} label="State">
            <MenuItem value=""><em>Select a State</em></MenuItem>
            {US_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </OpenOnFocusSelect>
          {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}

export function PersonFieldGroup({
  prefix,
  control,
  required,
}: {
  prefix: string
  control: Control<OrderRequestFormInputs>
  required?: boolean
}) {
  return (
    <>
      {/* Person Contact Info */}
      <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
        <Grid item xs={12} sm={12} md={4}>
          <Controller
            name={`${prefix}.FirstName` as any}
            control={control}
            rules={required ? { required: 'First Name is required', ...TEXT_RULES.Name } : TEXT_RULES.Name}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="First Name" required={required} {...fieldErrorProps(fieldState, required)} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Controller
            name={`${prefix}.MiddleName` as any}
            control={control}
            rules={TEXT_RULES.Name}
            render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Middle Name" error={!!fieldState.error} helperText={fieldState.error?.message} />}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Controller
            name={`${prefix}.LastName` as any}
            control={control}
            rules={required ? { required: 'Last Name is required', ...TEXT_RULES.Name } : TEXT_RULES.Name}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Last Name" required={required} {...fieldErrorProps(fieldState, required)} />
            )}
          />
        </Grid>
      </Grid>

      <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name={`${prefix}.Email` as any}
            control={control}
            rules={{ pattern: EMAIL_PATTERN, ...TEXT_RULES.Email }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Email" type="email" error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name={`${prefix}.Phone` as any}
            control={control}
            rules={{ pattern: PHONE_PATTERN }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Phone" error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
      </Grid>
    </>
  )
}

function PartyAddressSection({
  contactType,
  partyIndex,
  control,
}: {
  contactType: 'Buyer' | 'Seller'
  partyIndex: number
  control: Control<OrderRequestFormInputs>
}) {
  const prefix = `Parties.${contactType}.${partyIndex}.Address`
  const sameAsProperty = useWatch({ control, name: `${prefix}.SameAsProperty` as any })
  const [watchAddr1, watchAddr2, watchCity, watchState, watchZip] = useWatch({
    control,
    name: [
      `${prefix}.Address1` as any,
      `${prefix}.Address2` as any,
      `${prefix}.City` as any,
      `${prefix}.State` as any,
      `${prefix}.Zip` as any,
    ],
  })
  const anyFilled = !sameAsProperty && !!(watchAddr1 || watchAddr2 || watchCity || watchState || watchZip)

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Controller
        name={`${prefix}.SameAsProperty` as any}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox checked={field.value} onChange={field.onChange} inputProps={{ onKeyDown: checkboxEnterKeyDown(field) as any }} />}
            label="Contact Address same as Property Address"
          />
        )}
      />
      {!sameAsProperty && (
        <>
          <Typography variant="caption" color="text.secondary">Contact Address</Typography>
          <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name={`${prefix}.Address1` as any}
                control={control}
                rules={{ ...TEXT_RULES.Address, validate: { ...TEXT_RULES.Address.validate, requiredWhenFilled: (v) => !anyFilled || !!v || 'Address 1 is required' } }}
                render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Address 1" required={anyFilled} error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name={`${prefix}.Address2` as any}
                control={control}
                rules={TEXT_RULES.Address}
                render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Address 2" error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
          </Grid>
          <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
            <Grid item xs={12} sm={12} md={4}>
              <Controller
                name={`${prefix}.City` as any}
                control={control}
                rules={{ ...TEXT_RULES.City, validate: { requiredWhenFilled: (v) => !anyFilled || !!v || 'City is required' } }}
                render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="City" required={anyFilled} error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <StateSelect name={`${prefix}.State`} control={control} required={anyFilled} />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Controller
                name={`${prefix}.Zip` as any}
                control={control}
                rules={{ validate: (v) => !anyFilled || !!v || 'Zip is required', pattern: ZIP_PATTERN }}
                render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Zip" required={anyFilled} error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
        </>
      )}
    </Box>
  )
}

function OrgPeopleFieldArray({
  contactType,
  partyIndex,
  control,
}: {
  contactType: 'Buyer' | 'Seller'
  partyIndex: number
  control: Control<OrderRequestFormInputs>
}) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `Parties.${contactType}.${partyIndex}.OrgPeople` as any,
  })

  return (
    <>
      {fields.map((field, i) => (
        <Box key={field.id}>
          {fields.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Contact Person {i + 1}</Typography>
              <Button size="small" color="error" type="button" onClick={() => remove(i)}>Remove</Button>
            </Box>
          )}
          <PersonFieldGroup
            prefix={`Parties.${contactType}.${partyIndex}.OrgPeople.${i}`}
            control={control}
            required={i === 0}
          />
          {i < fields.length - 1 && <Divider sx={{ my: 1.5 }} />}
        </Box>
      ))}
      <Button
        startIcon={<AddCircleOutline />}
        size="small"
        variant="text"
        type="button"
        onClick={() => append({ ...defaultPerson })}
        sx={{ mt: 0.5 }}
      >
        Add Contact Person
      </Button>
    </>
  )
}

export function PartySection({
  contactType,
  index,
  control,
  required,
  onRemove,
}: {
  contactType: 'Buyer'|'Seller'
  index: number
  control: Control<OrderRequestFormInputs>
  required?: boolean
  onRemove?: () => void
}) {
  const partyType = useWatch({ control, name: `Parties.${contactType}.${index}.Type` })
  const isMarried = partyType === 'married'
  const isOrganization = partyType === 'organization'
  const isSectionRequired = !!required

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{contactType === 'Buyer' ? 'Buyer/Borrower' : 'Seller'} {index + 1}</Typography>
        <Button size="small" color="error" type="button" onClick={onRemove} sx={{ visibility: onRemove ? 'visible' : 'hidden' }}>Remove</Button>
      </Box>

      <Controller
        name={`Parties.${contactType}.${index}.Type`}
        control={control}
        render={({ field }) => (
          <FormControl component="fieldset" sx={{ mb: 1 }}>
            <RadioGroup row {...field}>
              <FormControlLabel value="individual" control={<Radio size="small" />} label="Individual" />
              <FormControlLabel value="married" control={<Radio size="small" />} label="Married" />
              <FormControlLabel value="organization" control={<Radio size="small" />} label="Organization" />
            </RadioGroup>
          </FormControl>
        )}
      />

      {isOrganization && (
        <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name={`Parties.${contactType}.${index}.OrganizationName`}
              control={control}
              rules={isSectionRequired ? { required: 'Organization name is required', ...TEXT_RULES.OrganizationName } : TEXT_RULES.OrganizationName}
              render={({ field, fieldState }) => (
                <TextField {...field} {...textFieldProps} label="Organization Name" required={isSectionRequired} {...fieldErrorProps(fieldState, isSectionRequired)} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller 
              name={`Parties.${contactType}.${index}.OrganizationType`} 
              control={control}
              rules={isSectionRequired ? { required: 'Organization type is required' } : undefined}
              render={({ field, fieldState }) => (
                <FormControl required={isSectionRequired} error={isSectionRequired && !!fieldState.error} fullWidth margin="normal">
                  <InputLabel>Type of Organization</InputLabel>
                  <OpenOnFocusSelect {...field} {...selectFieldProps} label="Type of Organization">
                    {ORGANIZATION_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </OpenOnFocusSelect>
                  {isSectionRequired && fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      )}

      <PartyAddressSection contactType={contactType} partyIndex={index} control={control} />

      {isMarried && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Primary Borrower
        </Typography>
      )}
      {isOrganization ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Contact Person(s)
          </Typography>
          <OrgPeopleFieldArray contactType={contactType} partyIndex={index} control={control} />
        </>
      ) : (
        <PersonFieldGroup prefix={`Parties.${contactType}.${index}.Primary`} control={control} required={isSectionRequired} />
      )}

      {isMarried && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Spouse
          </Typography>
          <PersonFieldGroup prefix={`Parties.${contactType}.${index}.Spouse`} control={control} required={isSectionRequired} />
        </>
      )}
    </Box>
  )
}

/* Lender Information */
export function LenderSection({
  index,
  control,
  required,
  onRemove,
}: {
  index: number
  control: Control<OrderRequestFormInputs>
  required?: boolean
  onRemove?: () => void
}) {
  const isRequesterLender = useWatch({control, name: 'AgentRole' as const}) === "Lender"
  const lenderSetToRequester = useWatch({control, name: `Lenders.${index}.SameAsRole` as any})
  const isSectionRequired = !!required
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
      {isRequesterLender && <Controller
        name={`Lenders.${index}.SameAsRole` as any}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox checked={field.value ?? false} onChange={field.onChange} inputProps={{ onKeyDown: checkboxEnterKeyDown(field) as any }} />}
            label="Lender is the Requester for this Title Order"
          />
        )}
      />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{'Lender'} {index + 1}</Typography>
        <Button size="small" color="error" type="button" onClick={onRemove} sx={{ visibility: onRemove ? 'visible' : 'hidden' }}>Remove</Button>
      </Box>

      <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
        {!lenderSetToRequester && <Grid item xs={12} sm={12} md={6}>
          <Controller name={`Lenders.${index}.LenderName`} control={control} rules={TEXT_RULES.LenderName}
            render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="New Lender Name" error={!!fieldState.error} helperText={fieldState.error?.message} />}
          />
        </Grid>}
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`Lenders.${index}.LoanAmount`} control={control} rules={isSectionRequired ? { pattern: LOAN_AMOUNT_PATTERN, required: 'Loan amount is required', ...TEXT_RULES.LoanAmount } : { pattern: LOAN_AMOUNT_PATTERN, ...TEXT_RULES.LoanAmount }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Loan Amount" required={isSectionRequired}
                error={!!fieldState.error && (isSectionRequired || fieldState.error.type !== 'required')} helperText={(isSectionRequired || fieldState.error?.type !== 'required') ? fieldState.error?.message : undefined}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`Lenders.${index}.LoanNumber`} control={control} rules={isSectionRequired ? { required: 'Loan number is required', ...TEXT_RULES.LoanNumber } : TEXT_RULES.LoanNumber}
            render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Loan Number" required={isSectionRequired} {...fieldErrorProps(fieldState, isSectionRequired)} />}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`Lenders.${index}.FinancingType`} control={control} rules={isSectionRequired ? { required: 'Financing type is required' } : undefined}
            render={({ field, fieldState }) => (
              <FormControl fullWidth margin="normal" required={isSectionRequired} error={isSectionRequired && !!fieldState.error}>
                <InputLabel>Financing Type</InputLabel>
                <OpenOnFocusSelect {...field} {...selectFieldProps} label="Financing Type">
                  <MenuItem value=""><em>Select Type</em></MenuItem>
                  {FINANCING_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </OpenOnFocusSelect>
                {isSectionRequired && fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        {!lenderSetToRequester &&
        <>
          <Grid item xs={12} sm={12} md={6}>
            <Controller name={`Lenders.${index}.LenderFirstName`} control={control} rules={TEXT_RULES.Name}
              render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Lender First Name" error={!!fieldState.error} helperText={fieldState.error?.message} />}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller name={`Lenders.${index}.LenderLastName`} control={control} rules={TEXT_RULES.Name}
              render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Lender Last Name" error={!!fieldState.error} helperText={fieldState.error?.message} />}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller name={`Lenders.${index}.LenderEmail`} control={control} rules={{ pattern: EMAIL_PATTERN, ...TEXT_RULES.Email }}
              render={({ field, fieldState }) => (
                <TextField {...field} {...textFieldProps} label="Lender Email" type="email" error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller name={`Lenders.${index}.LenderPhone`} control={control} rules={{ pattern: PHONE_PATTERN }}
              render={({ field, fieldState }) => (
                <TextField {...field} {...textFieldProps} label="Lender Phone Number" error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
        </>
        }
      </Grid>
    </Box>  
  )
}


/** Enabled custom fields with a known type. Ones with a missing/unknown type can't be rendered, so they're skipped entirely (otherwise a required one would block submit) */
export const getRenderableCustomFields = (fields: OrderRequestField[] | undefined): OrderRequestField[] =>
  fields?.filter((field) => field.Enabled && (orderRequestFieldTypes as readonly string[]).includes(field.type)) ?? []

/** Pairs each rendered custom field's label with its entered value (by position), as the submitted { Name, Value } list */
export const buildCustomFieldsPayload = (
  fields: OrderRequestField[],
  values: OrderRequestFormInputs['CustomFields'],
): NonNullable<OrderRequestFormInputs['CustomFields']> =>
  fields.map((field, ind) => ({ Name: field.label, Value: values?.[ind]?.Value ?? '' }))

/** Maps the form's attorneys to the submitted payload, keyed by type, for only the attorney sections currently shown */
export const buildAttorneysPayload = (attorneys: AttorneysInfo | undefined, shownTypes: AttorneyType[]): AttorneysInfo =>
  shownTypes.reduce<AttorneysInfo>((acc, type) => {
    const attorney = attorneys?.[type]
    if (!attorney) return acc
    acc[type] = {
      FirstName: attorney.FirstName,
      LastName: attorney.LastName,
      Email: attorney.Email,
      Phone: attorney.Phone,
      Fax: attorney.Fax || undefined,
      CompanyName: attorney.CompanyName,
      CompanyAddress1: attorney.CompanyAddress1 || undefined,
      CompanyAddress2: attorney.CompanyAddress2 || undefined,
      CompanyCity: attorney.CompanyCity || undefined,
      CompanyState: attorney.CompanyState || undefined,
      CompanyZip: attorney.CompanyZip || undefined,
    }
    return acc
  }, {})

/* Attorney Information */
export function AttorneySection({
  attorneyType,
  control,
  onRemove,
}: {
  attorneyType: AttorneyType
  control: Control<OrderRequestFormInputs>
  onRemove: () => void
}) {
  const name = `Attorneys.${attorneyType}` as const
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{attorneyLabel(attorneyType)}</Typography>
        <Button size="small" color="error" type="button" onClick={onRemove}>Remove</Button>
      </Box>

      <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`${name}.FirstName`} control={control} rules={{ required: 'First Name is required', ...TEXT_RULES.Name }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="First Name" required error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`${name}.LastName`} control={control} rules={{ required: 'Last Name is required', ...TEXT_RULES.Name }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Last Name" required error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`${name}.Email`} control={control} rules={{ pattern: EMAIL_PATTERN, required: 'Email is required', ...TEXT_RULES.Email }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Email" type="email" required error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`${name}.Phone`} control={control} rules={{ pattern: PHONE_PATTERN, required: "Phone is required" }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Phone" required error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`${name}.Fax`} control={control} rules={{ pattern: PHONE_PATTERN }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Fax" error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`${name}.CompanyName`} control={control} rules={{ required: 'Company Name is required', ...TEXT_RULES.CompanyName }}
            render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} required error={!!fieldState.error} helperText={fieldState.error?.message} label="Company Name" />}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`${name}.CompanyAddress1`} control={control} rules={TEXT_RULES.Address}
            render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Company Address 1" error={!!fieldState.error} helperText={fieldState.error?.message} />}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller name={`${name}.CompanyAddress2`} control={control} rules={TEXT_RULES.Address}
            render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Company Address 2" error={!!fieldState.error} helperText={fieldState.error?.message} />}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Controller name={`${name}.CompanyCity`} control={control} rules={TEXT_RULES.City}
            render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Company City" error={!!fieldState.error} helperText={fieldState.error?.message} />}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <StateSelect name={`${name}.CompanyState`} control={control} />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Controller name={`${name}.CompanyZip`} control={control} rules={{ pattern: ZIP_PATTERN }}
            render={({ field, fieldState }) => (
              <TextField {...field} {...textFieldProps} label="Company Zip Code" error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
